import { useQuery } from "@tanstack/react-query";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { apiGet } from "@/lib/api-client";
import { asRecord } from "@/lib/type-utils";

/**
 * Render a /health check value as a short string. Values can be strings
 * ("Connected"), objects with a status field, or plain object maps (the
 * `providers` check) — the latter previously stringified to
 * "[object Object]" on the live dashboard.
 */
function summarizeCheck(value: unknown): string {
  if (value == null) return "unknown";
  if (typeof value !== "object") return String(value);
  const record = asRecord(value);
  if (typeof record.status === "string") return record.status;
  const parts = Object.entries(record)
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${typeof v === "object" && v !== null ? "…" : String(v)}`);
  const extra = Object.keys(record).length - parts.length;
  return parts.length > 0
    ? parts.join(", ") + (extra > 0 ? `, +${extra} more` : "")
    : "—";
}

function statusTone(status: string): string {
  switch (status) {
    case "healthy":
      return "bg-green-400";
    case "degraded":
      return "bg-amber-400";
    default:
      return "bg-red-400";
  }
}

export function HealthMatrix() {
  // Reads the unauthenticated load-balancer /health, complementing the
  // authenticated /api/health/deep used elsewhere on the ops page.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["health"],
    queryFn: () => apiGet<Record<string, unknown>>("/health"),
    refetchInterval: 30_000,
  });

  const checks = asRecord(data?.checks);
  const status = typeof data?.status === "string" ? data.status : "unknown";

  return (
    <Panel title="Health Matrix" description="Load-balancer /health view">
      {isLoading ? (
        <LoadingState text="Checking health..." />
      ) : isError ? (
        <QueryErrorState what="load-balancer health check" onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-1.5">
          <div className="mb-3 flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${statusTone(status)}`} />
            <span className="text-sm font-medium capitalize text-white/80">{status}</span>
          </div>
          {Object.entries(checks).map(([key, value]) => {
            return (
              <div key={key} className="flex items-center justify-between rounded bg-white/5 px-2 py-1.5">
                <span className="text-xs text-white/60">{key}</span>
                <span className="text-xs capitalize text-white/70">{summarizeCheck(value)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
